import React, { useState, useEffect } from 'react';
import './EmployeeManagementDashboard.css'; // Import a separate CSS file

// Basic UI Components
const Button = ({ children, type = "button", onClick, className = "" }) => (
  <button
    type={type}
    onClick={onClick}
    className={`button ${className}`}
  >
    {children}
  </button>
);

const Input = ({ name, type = "text", label, onChange, value, required = false }) => (
  <div className="form-field">
    <label>{label}</label>
    <input
      name={name}
      type={type}
      onChange={onChange}
      value={value}
      required={required}
    />
  </div>
);

const Select = ({ name, label, options, onChange, value, required = false }) => (
  <div className="form-field">
    <label>{label}</label>
    <select
      name={name}
      onChange={onChange}
      value={value}
      required={required}
    >
      <option value="">Select an option</option>
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const Checkbox = ({ label, checked, onChange }) => (
  <div className="checkbox-container">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
    />
    <label>{label}</label>
  </div>
);

// Employee Form Modal
const EmployeeForm = ({ employee, onClose, onSubmit }) => {
  const dialogRef = React.useRef(null);
  
  const [formData, setFormData] = useState({
    id: employee?.id || '',
    firstName: employee?.firstName || '',
    lastName: employee?.lastName || '',
    email: employee?.email || '',
    department: employee?.department || '',
    position: employee?.position || '',
    employmentType: employee?.employmentType || 'full-time',
    employeeId: employee?.employeeId || '',
    hireDate: employee?.hireDate || '',
    status: employee?.status || 'active',
    privileges: employee?.privileges || {
      admin: false,
      investigator: false,
      reporter: true,
      viewer: true
    }
  });
  
  const [errors, setErrors] = useState({});
  
  // Open dialog when component mounts
  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.showModal();
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePrivilegeChange = (privilege) => {
    setFormData((prev) => ({
      ...prev,
      privileges: {
        ...prev.privileges,
        [privilege]: !prev.privileges[privilege]
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email is invalid";
    if (!formData.department.trim()) newErrors.department = "Department is required";
    if (!formData.position.trim()) newErrors.position = "Position is required";
    if (!formData.employeeId.trim()) newErrors.employeeId = "Employee ID is required";
    if (!formData.hireDate) newErrors.hireDate = "Hire date is required";
    
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    onSubmit(formData);
    dialogRef.current.close();
  };

  const handleClose = () => {
    dialogRef.current.close();
    onClose();
  };

  return (
    <dialog ref={dialogRef} className="modal" onClose={onClose}>
      <div className="modal-content">
        <h2 className="modal-title">
          {employee ? 'Edit Employee' : 'Add New Employee'}
        </h2>
        
        <form onSubmit={handleSubmit} className="form">
          <div className="form-row">
            <Input 
              name="firstName" 
              label="First Name" 
              value={formData.firstName}
              onChange={handleChange} 
              required 
              className={errors.firstName ? "error" : ""}
            />
            
            <Input 
              name="lastName" 
              label="Last Name" 
              value={formData.lastName}
              onChange={handleChange} 
              required
              className={errors.lastName ? "error" : ""}
            />
          </div>
          
          {errors.firstName && <p className="error-text">{errors.firstName}</p>}
          {errors.lastName && <p className="error-text">{errors.lastName}</p>}
          
          <Input 
            name="email" 
            type="email"
            label="Email" 
            value={formData.email}
            onChange={handleChange} 
            required
            className={errors.email ? "error" : ""}
          />
          {errors.email && <p className="error-text">{errors.email}</p>}
          
          <div className="form-row">
            <Input 
              name="department" 
              label="Department" 
              value={formData.department}
              onChange={handleChange} 
              required
              className={errors.department ? "error" : ""}
            />
            
            <Input 
              name="position" 
              label="Position" 
              value={formData.position}
              onChange={handleChange} 
              required
              className={errors.position ? "error" : ""}
            />
          </div>
          
          {errors.department && <p className="error-text">{errors.department}</p>}
          {errors.position && <p className="error-text">{errors.position}</p>}
          
          <div className="form-row">
            <Select 
              name="employmentType" 
              label="Employment Type" 
              options={[
                { value: 'full-time', label: 'Full Time' },
                { value: 'part-time', label: 'Part Time' },
                { value: 'contractor', label: 'Contractor' },
                { value: 'intern', label: 'Intern' }
              ]}
              value={formData.employmentType}
              onChange={handleChange} 
              required
            />
            
            <Input 
              name="employeeId" 
              label="Employee ID" 
              value={formData.employeeId}
              onChange={handleChange} 
              required
              className={errors.employeeId ? "error" : ""}
            />
          </div>
          
          {errors.employeeId && <p className="error-text">{errors.employeeId}</p>}
          
          <div className="form-row">
            <Input 
              name="hireDate" 
              type="date"
              label="Hire Date" 
              value={formData.hireDate}
              onChange={handleChange} 
              required
              className={errors.hireDate ? "error" : ""}
            />
            
            <Select 
              name="status" 
              label="Status" 
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'on-leave', label: 'On Leave' },
                { value: 'terminated', label: 'Terminated' }
              ]}
              value={formData.status}
              onChange={handleChange} 
              required
            />
          </div>
          
          {errors.hireDate && <p className="error-text">{errors.hireDate}</p>}
          
          <div className="privileges-section">
            <label>Privileges</label>
            <div className="privileges-container">
              <Checkbox 
                label="Admin (Full system access)" 
                checked={formData.privileges.admin} 
                onChange={() => handlePrivilegeChange('admin')} 
              />
              <Checkbox 
                label="Investigator (Can investigate incidents)" 
                checked={formData.privileges.investigator} 
                onChange={() => handlePrivilegeChange('investigator')} 
              />
              <Checkbox 
                label="Reporter (Can report incidents)" 
                checked={formData.privileges.reporter} 
                onChange={() => handlePrivilegeChange('reporter')} 
              />
              <Checkbox 
                label="Viewer (Can view incidents)" 
                checked={formData.privileges.viewer} 
                onChange={() => handlePrivilegeChange('viewer')} 
              />
            </div>
          </div>
          
          <div className="form-actions">
            <Button 
              onClick={handleClose}
              className="button-secondary"
            >
              Cancel
            </Button>
            <Button type="submit">
              {employee ? 'Update Employee' : 'Add Employee'}
            </Button>
          </div>
        </form>
      </div>
    </dialog>
  );
};

// Confirm Delete Modal
const ConfirmDeleteModal = ({ employee, onClose, onConfirm }) => {
  const dialogRef = React.useRef(null);
  
  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.showModal();
    }
  }, []);

  return (
    <dialog ref={dialogRef} className="modal" onClose={onClose}>
      <div className="modal-content">
        <h2 className="modal-title">Confirm Employee Removal</h2>
        <p className="confirm-message">
          Are you sure you want to remove <strong>{employee.firstName} {employee.lastName}</strong> from the system? This action cannot be undone.
        </p>
        
        <div className="form-actions">
          <Button 
            onClick={onClose}
            className="button-secondary"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              onConfirm(employee.id);
              dialogRef.current.close();
            }}
            className="button-danger"
          >
            Remove Employee
          </Button>
        </div>
      </div>
    </dialog>
  );
};

// Filter Component
const EmployeeFilter = ({ onFilterChange, departments }) => {
  const [filters, setFilters] = useState({
    department: '',
    status: '',
    search: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  return (
    <div className="filter-container">
      <h3>Filter Employees</h3>
      <div className="filter-controls">
        <div className="filter-field">
          <label>Search</label>
          <input
            type="text"
            name="search"
            placeholder="Search by name or ID..."
            value={filters.search}
            onChange={handleChange}
          />
        </div>
        
        <div className="filter-field">
          <label>Department</label>
          <select
            name="department"
            value={filters.department}
            onChange={handleChange}
          >
            <option value="">All Departments</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-field">
          <label>Status</label>
          <select
            name="status"
            value={filters.status}
            onChange={handleChange}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="on-leave">On Leave</option>
            <option value="terminated">Terminated</option>
          </select>
        </div>
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusClass = `status-badge status-${status}`;
  
  return (
    <span className={statusClass}>
      {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
    </span>
  );
};

// Main Employee Management Dashboard Component
const EmployeeManagementDashboard = () => {
  // Mock data for initial employees
  const initialEmployees = [
    {
      id: '1',
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      department: 'Operations',
      position: 'Safety Manager',
      employmentType: 'full-time',
      employeeId: 'EMP001',
      hireDate: '2022-01-15',
      status: 'active',
      privileges: {
        admin: true,
        investigator: true,
        reporter: true,
        viewer: true
      }
    },
    {
      id: '2',
      firstName: 'Jane',
      lastName: 'Smith',
      email: 'jane.smith@example.com',
      department: 'Human Resources',
      position: 'HR Specialist',
      employmentType: 'full-time',
      employeeId: 'EMP002',
      hireDate: '2022-03-22',
      status: 'active',
      privileges: {
        admin: false,
        investigator: false,
        reporter: true,
        viewer: true
      }
    },
    {
      id: '3',
      firstName: 'Robert',
      lastName: 'Johnson',
      email: 'robert.johnson@example.com',
      department: 'Engineering',
      position: 'Safety Engineer',
      employmentType: 'full-time',
      employeeId: 'EMP003',
      hireDate: '2021-11-08',
      status: 'active',
      privileges: {
        admin: false,
        investigator: true,
        reporter: true,
        viewer: true
      }
    },
    {
      id: '4',
      firstName: 'Emily',
      lastName: 'Brown',
      email: 'emily.brown@example.com',
      department: 'Operations',
      position: 'Field Inspector',
      employmentType: 'full-time',
      employeeId: 'EMP004',
      hireDate: '2023-02-18',
      status: 'on-leave',
      privileges: {
        admin: false,
        investigator: true,
        reporter: true,
        viewer: true
      }
    },
    {
      id: '5',
      firstName: 'Michael',
      lastName: 'Wilson',
      email: 'michael.wilson@example.com',
      department: 'IT',
      position: 'IT Specialist',
      employmentType: 'contractor',
      employeeId: 'CON001',
      hireDate: '2023-05-10',
      status: 'active',
      privileges: {
        admin: false,
        investigator: false,
        reporter: true,
        viewer: true
      }
    }
  ];
  
  const [employees, setEmployees] = useState(initialEmployees);
  const [filteredEmployees, setFilteredEmployees] = useState(initialEmployees);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deletingEmployee, setDeletingEmployee] = useState(null);
  const [filters, setFilters] = useState({ department: '', status: '', search: '' });
  
  // Get unique departments for filter dropdown
  const departments = [...new Set(employees.map(emp => emp.department))];
  
  // Filter employees based on current filters
  useEffect(() => {
    let filtered = [...employees];
    
    if (filters.department) {
      filtered = filtered.filter(emp => emp.department === filters.department);
    }
    
    if (filters.status) {
      filtered = filtered.filter(emp => emp.status === filters.status);
    }
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.firstName.toLowerCase().includes(searchLower) ||
        emp.lastName.toLowerCase().includes(searchLower) ||
        emp.employeeId.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredEmployees(filtered);
  }, [employees, filters]);
  
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  const handleAddEmployee = (newEmployee) => {
    const newId = (Math.max(...employees.map(emp => parseInt(emp.id))) + 1).toString();
    setEmployees([...employees, { ...newEmployee, id: newId }]);
    setShowAddForm(false);
  };
  
  const handleUpdateEmployee = (updatedEmployee) => {
    setEmployees(employees.map(emp => 
      emp.id === updatedEmployee.id ? updatedEmployee : emp
    ));
    setEditingEmployee(null);
  };
  
  const handleDeleteEmployee = (employeeId) => {
    setEmployees(employees.filter(emp => emp.id !== employeeId));
    setDeletingEmployee(null);
  };
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Employee Management</h1>
        <Button onClick={() => setShowAddForm(true)}>
          Add Employee
        </Button>
      </div>
      
      <EmployeeFilter 
        onFilterChange={handleFilterChange}
        departments={departments}
      />
      
      <div className="table-container">
        <table className="employee-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>ID</th>
              <th>Department</th>
              <th>Position</th>
              <th>Status</th>
              <th>Privileges</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map(employee => (
                <tr key={employee.id}>
                  <td>
                    <div className="employee-info">
                      <div className="employee-name">{employee.firstName} {employee.lastName}</div>
                      <div className="employee-email">{employee.email}</div>
                    </div>
                  </td>
                  <td>{employee.employeeId}</td>
                  <td>{employee.department}</td>
                  <td>{employee.position}</td>
                  <td>
                    <StatusBadge status={employee.status} />
                  </td>
                  <td>
                    <div className="privileges-list">
                      {employee.privileges.admin && 
                        <span className="privilege-badge privilege-admin">Admin</span>
                      }
                      {employee.privileges.investigator && 
                        <span className="privilege-badge privilege-investigator">Investigator</span>
                      }
                      {employee.privileges.reporter && 
                        <span className="privilege-badge privilege-reporter">Reporter</span>
                      }
                      {employee.privileges.viewer && 
                        <span className="privilege-badge privilege-viewer">Viewer</span>
                      }
                    </div>
                  </td>
                  <td className="actions-cell">
                    <div className="action-buttons">
                      <Button
                        onClick={() => setEditingEmployee(employee)}
                        className="button-edit"
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => setDeletingEmployee(employee)}
                        className="button-delete"
                      >
                        Remove
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-results">
                  No employees found matching the current filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {showAddForm && (
        <EmployeeForm 
          onClose={() => setShowAddForm(false)}
          onSubmit={handleAddEmployee}
        />
      )}
      
      {editingEmployee && (
        <EmployeeForm 
          employee={editingEmployee}
          onClose={() => setEditingEmployee(null)}
          onSubmit={handleUpdateEmployee}
        />
      )}
      
      {deletingEmployee && (
        <ConfirmDeleteModal
          employee={deletingEmployee}
          onClose={() => setDeletingEmployee(null)}
          onConfirm={handleDeleteEmployee}
        />
      )}
    </div>
  );
};

export default EmployeeManagementDashboard;